/**
 * Centralized logging utility for the application
 * Provides structured logging with different levels and environments
 */

export const LogLevel = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3,
} as const;

export type LogLevel = typeof LogLevel[keyof typeof LogLevel];

export interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: Date;
  context?: string;
  data?: unknown;
}

const LogLevelNames: Record<LogLevel, string> = {
  [LogLevel.ERROR]: 'ERROR',
  [LogLevel.WARN]: 'WARN',
  [LogLevel.INFO]: 'INFO',
  [LogLevel.DEBUG]: 'DEBUG',
};

class Logger {
  private isDevelopment = import.meta.env.DEV;
  private currentLevel: LogLevel = this.isDevelopment ? LogLevel.DEBUG : LogLevel.WARN;

  private formatMessage(level: LogLevel, message: string, context?: string, data?: unknown): string {
    const timestamp = new Date().toISOString();
    const levelStr = LogLevelNames[level];
    const contextStr = context ? ` [${context}]` : '';
    const dataStr = data ? ` ${JSON.stringify(data)}` : '';
    
    return `[${timestamp}] ${levelStr}${contextStr}: ${message}${dataStr}`;
  }

  private shouldLog(level: LogLevel): boolean {
    return level <= this.currentLevel;
  }

  public error(message: string, context?: string, data?: unknown): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(this.formatMessage(LogLevel.ERROR, message, context, data));
    }
  }

  public warn(message: string, context?: string, data?: unknown): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(this.formatMessage(LogLevel.WARN, message, context, data));
    }
  }

  public info(message: string, context?: string, data?: unknown): void {
    if (this.shouldLog(LogLevel.INFO)) {
      // eslint-disable-next-line no-console
      console.info(this.formatMessage(LogLevel.INFO, message, context, data));
    }
  }

  public debug(message: string, context?: string, data?: unknown): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      // eslint-disable-next-line no-console
      console.log(this.formatMessage(LogLevel.DEBUG, message, context, data));
    }
  }

  public setLevel(level: LogLevel): void {
    this.currentLevel = level;
  }
}

// Export singleton instance
export const logger = new Logger();

// Export convenience functions
export const log = {
  error: (message: string, context?: string, data?: unknown) => logger.error(message, context, data),
  warn: (message: string, context?: string, data?: unknown) => logger.warn(message, context, data),
  info: (message: string, context?: string, data?: unknown) => logger.info(message, context, data),
  debug: (message: string, context?: string, data?: unknown) => logger.debug(message, context, data),
};
